/**
 * Inventory products and units (inventory items per product).
 * Persisted in localStorage so Inventory and ProductDetail stay in sync.
 */

import { getTimEPartsInventory } from './timEInventoryList';
import { getBimEPartsInventory } from './bimEInventoryList';
import { composeAssetTagSerial } from '../utils/assetTagBarcode';

export type ProductType = 'Robot' | 'Accessory' | 'Stock Item';

export type ProductCatalogStatus = 'Active' | 'Void';

export interface InventoryProduct {
  id: string;
  name: string;
  type: ProductType;
  sku: string;
  availability: number;
  inUse: number;
  /** When set, item appears on dashboard low-stock card when availability < this number; highlight yellow when below buffer, red when < 10. */
  lowStockBuffer?: number | null;
  /** Catalog lifecycle: voided products cannot be edited except this field (until set back to Active). */
  catalogStatus?: ProductCatalogStatus;
  /** Replacement cost per unit (free text, e.g. "$1,200") for reporting and valuation context. */
  replacementCostPerItem?: string;
}

export type InventoryUnitStatus = 'Deployed' | 'In Storage' | 'Repair' | 'Out of Order' | 'Event' | 'In-Route';
export type InventoryUnitCondition = 'Working' | 'Broken';

export interface InventoryUnit {
  id: string;
  productId: string;
  countryOfOrigin: string;
  /** Asset-tag category (e.g. GARG), distinct from catalog product type */
  productType?: string;
  model: string;
  serialNumber: string;
  /** Original temporary serial (TMP-…) carried over from an incoming shipment, kept for reference/search. */
  tempSerialNumber?: string;
  status?: InventoryUnitStatus;
  condition?: InventoryUnitCondition;
  manufacturer?: string;
  /** City/location for TIM-E/BIM-E */
  location?: string;
  business?: string;
  installationDate?: string;
  trashBins?: number;
  linenBins?: number;
  rollingBases?: number;
  staticBases?: number;
  /** Bulk count for Stock Item rows (manufacturer + location); defaults to 1 for robots/accessories. */
  stockQuantity?: number;
  /** Fully assembled in warehouse / ready — Yes/No per row; drives TIM-E monthly report assembled vs not-assembled columns for that catalog product. */
  assembled?: boolean;
  /** @deprecated use status/condition instead */
  gen?: string;
}

/** Bulk count carried by a Stock Item row; blank/legacy values count as 1. */
export function stockRowQuantity(u: { stockQuantity?: number }): number {
  const q = Number(u.stockQuantity);
  return Number.isFinite(q) && q > 0 ? Math.floor(q) : 1;
}

/**
 * Stock rows record deployment through the unit status rather than a separate flag:
 * 'Deployed' means the items are sitting at a customer business, anything else means
 * they're at an office/warehouse and genuinely available.
 */
export function isStockRowDeployed(u: { status?: InventoryUnitStatus }): boolean {
  return u.status === 'Deployed';
}

/** Status to store for a stock row given the Deployed Yes/No choice. */
export function stockDeployedToStatus(deployed: boolean): InventoryUnitStatus {
  return deployed ? 'Deployed' : 'In Storage';
}

/**
 * Case-insensitive key for merging stock rows (same manufacturer + location +
 * deployed). Deployed is part of the key so a warehouse row and a deployed row that
 * happen to share a location (e.g. a city) stay separate instead of merging into one
 * count that would misreport availability.
 */
export function normalizeStockPlacementKey(
  manufacturer: string,
  location: string,
  deployed = false
): string {
  const m = manufacturer.toLowerCase().replace(/\s+/g, ' ').trim();
  const l = location.toLowerCase().replace(/\s+/g, ' ').trim();
  return `${m}\t${l}\t${deployed ? 'deployed' : 'stored'}`;
}

export function findStockItemRowIndex(
  units: InventoryUnit[],
  productId: string,
  manufacturer: string,
  location: string,
  deployed = false
): number {
  const key = normalizeStockPlacementKey(manufacturer, location, deployed);
  return units.findIndex(
    (u) =>
      u.productId === productId &&
      normalizeStockPlacementKey(u.manufacturer ?? '', u.location ?? '', isStockRowDeployed(u)) === key
  );
}

const STORAGE_PRODUCTS = 'inventory_products';
const STORAGE_UNITS = 'inventory_units';

export function genId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

// Initial product list from contract form 2 (no Monthly/One-time labels). Type Robot vs Accessory.
const INITIAL_PRODUCTS: Omit<InventoryProduct, 'id'>[] = [
  { name: 'TIM-E Bot', type: 'Robot', sku: '', availability: 0, inUse: 0 },
  { name: 'TIM-E Charger', type: 'Robot', sku: '', availability: 0, inUse: 0 },
  { name: 'Base - Metal', type: 'Accessory', sku: '', availability: 0, inUse: 0 },
  { name: 'Base - Static', type: 'Accessory', sku: '', availability: 0, inUse: 0 },
  { name: 'Insulated Food Transport', type: 'Accessory', sku: '', availability: 0, inUse: 0 },
  { name: 'Wheeled Bin', type: 'Accessory', sku: '', availability: 0, inUse: 0 },
  { name: 'Universal Platform', type: 'Accessory', sku: '', availability: 0, inUse: 0 },
  { name: 'Door Openers', type: 'Accessory', sku: '', availability: 0, inUse: 0 },
  { name: 'NeuralTech Brain', type: 'Accessory', sku: '', availability: 0, inUse: 0 },
  { name: 'Elevator Hardware + Software', type: 'Accessory', sku: '', availability: 0, inUse: 0 },
  { name: 'Luggage Cart', type: 'Accessory', sku: '', availability: 0, inUse: 0 },
  { name: 'Concession Bin - Tall', type: 'Accessory', sku: '', availability: 0, inUse: 0 },
  { name: 'Stacking Chair Cart', type: 'Accessory', sku: '', availability: 0, inUse: 0 },
  { name: 'Cargo Cart', type: 'Accessory', sku: '', availability: 0, inUse: 0 },
  { name: 'Housekeeping Cart', type: 'Accessory', sku: '', availability: 0, inUse: 0 },
  { name: 'BIM-E bot', type: 'Robot', sku: '', availability: 0, inUse: 0 },
  { name: 'Kebb-i', type: 'Robot', sku: '', availability: 0, inUse: 0 },
  { name: 'Mobile BIM-E', type: 'Robot', sku: '', availability: 0, inUse: 0 },
  { name: 'Plastic Bags', type: 'Accessory', sku: '', availability: 0, inUse: 0 },
  { name: 'Door Opener Hardware', type: 'Accessory', sku: '', availability: 0, inUse: 0 },
  { name: 'Handheld Tablet w/ App', type: 'Accessory', sku: '', availability: 0, inUse: 0 },
];

/** Catalog stock parts (matches server migration 050_inventory_stock_item.sql seed section). */
const STOCK_ITEM_SEED: Omit<InventoryProduct, 'id'>[] = [
  { name: '35 G Trash Cans', type: 'Stock Item', sku: '', availability: 0, inUse: 0 },
  { name: '35 G Trash lids', type: 'Stock Item', sku: '', availability: 0, inUse: 0 },
  { name: 'Esp 32 Autoxing Box', type: 'Stock Item', sku: '', availability: 0, inUse: 0 },
  { name: 'Door Openers', type: 'Stock Item', sku: '', availability: 0, inUse: 0 },
  { name: 'Elevator Openers', type: 'Stock Item', sku: '', availability: 0, inUse: 0 },
  { name: 'PCB', type: 'Stock Item', sku: '', availability: 0, inUse: 0 },
  { name: 'Lids with hole - Steel', type: 'Stock Item', sku: '', availability: 0, inUse: 0 },
  { name: 'UWB Module Decawave DWM1000 | RAK13801', type: 'Stock Item', sku: '', availability: 0, inUse: 0 },
  { name: 'Rasberry Pi', type: 'Stock Item', sku: '', availability: 0, inUse: 0 },
  { name: 'VL53L1X Time of Flight Distance Sensor', type: 'Stock Item', sku: '', availability: 0, inUse: 0 },
  { name: 'Octagon Bin steel', type: 'Stock Item', sku: '', availability: 0, inUse: 0 },
  { name: 'CR202', type: 'Stock Item', sku: '', availability: 0, inUse: 0 },
  { name: 'plastic kegs 5 gal', type: 'Stock Item', sku: '', availability: 0, inUse: 0 },
  { name: 'plastic keg filter kit', type: 'Stock Item', sku: '', availability: 0, inUse: 0 },
  { name: '1 Gal/4 lit', type: 'Stock Item', sku: '', availability: 0, inUse: 0 },
  { name: 'gas manifold', type: 'Stock Item', sku: '', availability: 0, inUse: 0 },
  { name: 'ball lock', type: 'Stock Item', sku: '', availability: 0, inUse: 0 },
  { name: 'manifold brackets', type: 'Stock Item', sku: '', availability: 0, inUse: 0 },
  { name: 'ball lock adapter', type: 'Stock Item', sku: '', availability: 0, inUse: 0 },
  { name: 'bal lock wuick disconect', type: 'Stock Item', sku: '', availability: 0, inUse: 0 },
  { name: '8l/2.1 gal', type: 'Stock Item', sku: '', availability: 0, inUse: 0 },
  { name: 'PLC Electornic Stack', type: 'Stock Item', sku: '', availability: 0, inUse: 0 },
  { name: 'RaspBerry Pi 5', type: 'Stock Item', sku: '', availability: 0, inUse: 0 },
  { name: 'Alluminum Frame Assembly Cut', type: 'Stock Item', sku: '', availability: 0, inUse: 0 },
  { name: 'Hood Assembly Frame Cover', type: 'Stock Item', sku: '', availability: 0, inUse: 0 },
  { name: 'Drip Tray', type: 'Stock Item', sku: '', availability: 0, inUse: 0 },
  { name: 'Stepper Motor', type: 'Stock Item', sku: '', availability: 0, inUse: 0 },
  { name: 'Toggle Buttons', type: 'Stock Item', sku: '', availability: 0, inUse: 0 },
  { name: 'Latching Power Button', type: 'Stock Item', sku: '', availability: 0, inUse: 0 },
  { name: 'Cup Drop Unit', type: 'Stock Item', sku: '', availability: 0, inUse: 0 },
  { name: 'Co2 Tank 20lb', type: 'Stock Item', sku: '', availability: 0, inUse: 0 },
  { name: 'Nitro Tank 5Lb', type: 'Stock Item', sku: '', availability: 0, inUse: 0 },
  { name: 'Aluminum Shaft for Cup Drop Assembly', type: 'Stock Item', sku: '', availability: 0, inUse: 0 },
  { name: 'Spinner Blade Stainless Steel', type: 'Stock Item', sku: '', availability: 0, inUse: 0 },
  { name: '20x60 Aluminum extriuosn cut 112mm', type: 'Stock Item', sku: '', availability: 0, inUse: 0 },
  { name: '3D Printed Spinner Body', type: 'Stock Item', sku: '', availability: 0, inUse: 0 },
  { name: '3D Printed Shaft Mount', type: 'Stock Item', sku: '', availability: 0, inUse: 0 },
  { name: '3D Printed Spinner Gear', type: 'Stock Item', sku: '', availability: 0, inUse: 0 },
  { name: '3D Printed Spinner Main Gear', type: 'Stock Item', sku: '', availability: 0, inUse: 0 },
  { name: '3D Printed Cup Drop Mount', type: 'Stock Item', sku: '', availability: 0, inUse: 0 },
  { name: '1" Foam Sheet', type: 'Stock Item', sku: '', availability: 0, inUse: 0 },
  { name: '3/4" Foam Sheet', type: 'Stock Item', sku: '', availability: 0, inUse: 0 },
  { name: 'Cut Foam Top', type: 'Stock Item', sku: '', availability: 0, inUse: 0 },
  { name: 'Cut Foam Sides', type: 'Stock Item', sku: '', availability: 0, inUse: 0 },
  { name: 'Cut Foam Back', type: 'Stock Item', sku: '', availability: 0, inUse: 0 },
  { name: 'Solenoid Valves 8mm Food Grade V2', type: 'Stock Item', sku: '', availability: 0, inUse: 0 },
  { name: 'Solenoid Valve v3 Stainless Steel', type: 'Stock Item', sku: '', availability: 0, inUse: 0 },
  { name: 'Titan Beverage Flow Meter', type: 'Stock Item', sku: '', availability: 0, inUse: 0 },
  { name: 'Homeing Sensor Panasonic', type: 'Stock Item', sku: '', availability: 0, inUse: 0 },
  { name: 'PhotoElectric Sensors Cup Detection', type: 'Stock Item', sku: '', availability: 0, inUse: 0 },
  { name: '5mmid x 8mm od Tubing FT', type: 'Stock Item', sku: '', availability: 0, inUse: 0 },
  { name: 'Gas Tubing ft', type: 'Stock Item', sku: '', availability: 0, inUse: 0 },
  { name: 'Soda System', type: 'Stock Item', sku: '', availability: 0, inUse: 0 },
  { name: 'R-Mini Solenoid (italian)', type: 'Stock Item', sku: '', availability: 0, inUse: 0 },
  { name: 'Primary Regulator', type: 'Stock Item', sku: '', availability: 0, inUse: 0 },
  { name: 'Secondary Regulator', type: 'Stock Item', sku: '', availability: 0, inUse: 0 },
  { name: 'POS Touch Screen + Pi', type: 'Stock Item', sku: '', availability: 0, inUse: 0 },
  { name: 'Square Space Ipad', type: 'Stock Item', sku: '', availability: 0, inUse: 0 },
  { name: 'Square Swivel Mount', type: 'Stock Item', sku: '', availability: 0, inUse: 0 },
  { name: 'Square Surface and wall mount', type: 'Stock Item', sku: '', availability: 0, inUse: 0 },
  { name: 'Glycol Chiller', type: 'Stock Item', sku: '', availability: 0, inUse: 0 },
  { name: 'Hall Effect Sensor', type: 'Stock Item', sku: '', availability: 0, inUse: 0 },
  { name: 'Low Profile Keg taps Kegco', type: 'Stock Item', sku: '', availability: 0, inUse: 0 },
  { name: 'Micro matic Keg Tap Regular', type: 'Stock Item', sku: '', availability: 0, inUse: 0 },
  { name: 'Micro Matic Secondary Regulator', type: 'Stock Item', sku: '', availability: 0, inUse: 0 },
  { name: '1/2" ABS Bin Lid', type: 'Stock Item', sku: '', availability: 0, inUse: 0 },
  { name: '2.5 LB Co2 Tank', type: 'Stock Item', sku: '', availability: 0, inUse: 0 },
  { name: 'Single Door black Fridge', type: 'Stock Item', sku: '', availability: 0, inUse: 0 },
  { name: 'XCK-1B Fridge', type: 'Stock Item', sku: '', availability: 0, inUse: 0 },
  { name: 'Vevor Automatic Wire Cutting Machine', type: 'Stock Item', sku: '', availability: 0, inUse: 0 },
  { name: '5 Gal OxeBar Kegs', type: 'Stock Item', sku: '', availability: 0, inUse: 0 },
  { name: '2gal OxeBar Keg Setup', type: 'Stock Item', sku: '', availability: 0, inUse: 0 },
  { name: '1gal OxeBar Keg Setup', type: 'Stock Item', sku: '', availability: 0, inUse: 0 },
  { name: 'Pinch Valve Solenoid', type: 'Stock Item', sku: '', availability: 0, inUse: 0 },
  { name: "2500' 22awg cable all colors", type: 'Stock Item', sku: '', availability: 0, inUse: 0 },
  { name: 'x2d 3d printer', type: 'Stock Item', sku: '', availability: 0, inUse: 0 },
  { name: 'White folding table 4ft', type: 'Stock Item', sku: '', availability: 0, inUse: 0 },
  { name: 'Shop Fan 20"', type: 'Stock Item', sku: '', availability: 0, inUse: 0 },
  { name: 'Cut Foam Front', type: 'Stock Item', sku: '', availability: 0, inUse: 0 },
  { name: '8mm Fittings Through Connection', type: 'Stock Item', sku: '', availability: 0, inUse: 0 },
  { name: '8mm Elbow Fitting', type: 'Stock Item', sku: '', availability: 0, inUse: 0 },
];

function mergeStockSeedInto(products: InventoryProduct[]): InventoryProduct[] {
  const names = new Set(products.map((p) => p.name));
  const additions: InventoryProduct[] = [];
  for (const row of STOCK_ITEM_SEED) {
    if (names.has(row.name)) continue;
    names.add(row.name);
    additions.push({ ...row, id: genId() });
  }
  if (additions.length === 0) return products;
  return [...products, ...additions];
}

function ensureInitialProducts(): InventoryProduct[] {
  try {
    const raw = localStorage.getItem(STORAGE_PRODUCTS);
    if (raw) {
      const parsed = JSON.parse(raw) as InventoryProduct[];
      if (Array.isArray(parsed) && parsed.length > 0) {
        let updated = parsed.map((p) => (p.name === 'BIM-E' ? { ...p, name: 'BIM-E bot' } : p));
        const afterBim = updated;
        updated = mergeStockSeedInto(updated);
        if (updated.length !== parsed.length || afterBim.some((p, i) => p.name !== parsed[i].name)) {
          localStorage.setItem(STORAGE_PRODUCTS, JSON.stringify(updated));
        }
        return updated;
      }
    }
  } catch {
    // ignore
  }
  const mergedTemplate = [
    ...INITIAL_PRODUCTS,
    ...STOCK_ITEM_SEED.filter((s) => !INITIAL_PRODUCTS.some((p) => p.name === s.name)),
  ];
  const withIds: InventoryProduct[] = mergedTemplate.map((p) => ({
    ...p,
    id: genId(),
  }));
  localStorage.setItem(STORAGE_PRODUCTS, JSON.stringify(withIds));
  return withIds;
}

export function getInventoryProducts(): InventoryProduct[] {
  return ensureInitialProducts();
}

export function setInventoryProducts(products: InventoryProduct[]): void {
  localStorage.setItem(STORAGE_PRODUCTS, JSON.stringify(products));
}

export function getInventoryUnits(): InventoryUnit[] {
  try {
    const raw = localStorage.getItem(STORAGE_UNITS);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as InventoryUnit[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function setInventoryUnits(units: InventoryUnit[]): void {
  localStorage.setItem(STORAGE_UNITS, JSON.stringify(units));
}

export function addProduct(name: string, type: ProductType, sku?: string): InventoryProduct {
  const products = getInventoryProducts();
  const newProduct: InventoryProduct = {
    id: genId(),
    name: name.trim(),
    type,
    sku: sku?.trim() ?? '',
    availability: 0,
    inUse: 0,
    replacementCostPerItem: '',
  };
  products.push(newProduct);
  setInventoryProducts(products);
  return newProduct;
}

export type AddInventoryUnitOptions = {
  productType?: string;
  status?: InventoryUnitStatus;
  condition?: InventoryUnitCondition;
  manufacturer?: string;
  location?: string;
  business?: string;
  installationDate?: string;
  trashBins?: number;
  linenBins?: number;
  rollingBases?: number;
  staticBases?: number;
  stockQuantity?: number;
  assembled?: boolean;
};

/** Returns a new product object (does not persist). Use with context setProducts. */
export function createNewProduct(
  name: string,
  type: ProductType,
  sku?: string,
  replacementCostPerItem?: string
): InventoryProduct {
  return {
    id: genId(),
    name: name.trim(),
    type,
    sku: sku?.trim() ?? '',
    availability: 0,
    inUse: 0,
    catalogStatus: 'Active',
    replacementCostPerItem: replacementCostPerItem?.trim() ?? '',
  };
}

/** Returns a new unit object (does not persist). Use with context setUnits. */
export function createNewInventoryUnit(
  productId: string,
  countryOfOrigin: string,
  model: string,
  serialNumber: string,
  options?: AddInventoryUnitOptions
): InventoryUnit {
  return {
    id: genId(),
    productId,
    countryOfOrigin: countryOfOrigin.trim(),
    ...(options?.productType != null && String(options.productType).trim() !== ''
      ? { productType: String(options.productType).trim() }
      : {}),
    model: model.trim(),
    serialNumber: composeAssetTagSerial(countryOfOrigin, options?.productType, serialNumber),
    ...(options?.status ? { status: options.status } : {}),
    ...(options?.condition ? { condition: options.condition } : {}),
    ...(options?.manufacturer?.trim() ? { manufacturer: options.manufacturer.trim() } : {}),
    ...(options?.location?.trim() ? { location: options.location.trim() } : {}),
    ...(options?.business?.trim() ? { business: options.business.trim() } : {}),
    ...(options?.installationDate?.trim() ? { installationDate: options.installationDate.trim() } : {}),
    ...(options?.trashBins !== undefined ? { trashBins: options.trashBins } : {}),
    ...(options?.linenBins !== undefined ? { linenBins: options.linenBins } : {}),
    ...(options?.rollingBases !== undefined ? { rollingBases: options.rollingBases } : {}),
    ...(options?.staticBases !== undefined ? { staticBases: options.staticBases } : {}),
    ...(options?.stockQuantity != null &&
    Number.isFinite(options.stockQuantity) &&
    Math.floor(options.stockQuantity) > 0
      ? { stockQuantity: Math.floor(options.stockQuantity) }
      : {}),
    assembled: options?.assembled !== false,
  };
}

export function addInventoryUnit(
  productId: string,
  countryOfOrigin: string,
  model: string,
  serialNumber: string,
  options?: AddInventoryUnitOptions
): InventoryUnit {
  const units = getInventoryUnits();
  const newUnit: InventoryUnit = {
    id: genId(),
    productId,
    countryOfOrigin: countryOfOrigin.trim(),
    ...(options?.productType != null && String(options.productType).trim() !== ''
      ? { productType: String(options.productType).trim() }
      : {}),
    model: model.trim(),
    serialNumber: composeAssetTagSerial(countryOfOrigin, options?.productType, serialNumber),
    ...(options?.status ? { status: options.status } : {}),
    ...(options?.condition ? { condition: options.condition } : {}),
    ...(options?.manufacturer?.trim() ? { manufacturer: options.manufacturer.trim() } : {}),
    ...(options?.location?.trim() ? { location: options.location.trim() } : {}),
    ...(options?.business?.trim() ? { business: options.business.trim() } : {}),
    ...(options?.installationDate?.trim() ? { installationDate: options.installationDate.trim() } : {}),
    ...(options?.trashBins !== undefined ? { trashBins: options.trashBins } : {}),
    ...(options?.linenBins !== undefined ? { linenBins: options.linenBins } : {}),
    ...(options?.rollingBases !== undefined ? { rollingBases: options.rollingBases } : {}),
    ...(options?.staticBases !== undefined ? { staticBases: options.staticBases } : {}),
    ...(options?.stockQuantity != null &&
    Number.isFinite(options.stockQuantity) &&
    Math.floor(options.stockQuantity) > 0
      ? { stockQuantity: Math.floor(options.stockQuantity) }
      : {}),
    assembled: options?.assembled !== false,
  };
  units.push(newUnit);
  setInventoryUnits(units);
  return newUnit;
}

export function getUnitsByProductId(productId: string, allUnits?: InventoryUnit[]): InventoryUnit[] {
  const list = allUnits ?? getInventoryUnits();
  return list.filter((u) => u.productId === productId);
}

/** Counts derived from unit status: In Storage (and missing/legacy status) = availability; Deployed + Event = in-use; Repair + Out of Order + In-Route = unavailable. */
export type ProductUnitStatusCounts = {
  availability: number;
  inUse: number;
  unavailable: number;
};

export function getProductAvailabilityAndInUse(
  productId: string,
  allUnits?: InventoryUnit[],
  _productCatalogType?: ProductType
): ProductUnitStatusCounts {
  const list = getUnitsByProductId(productId, allUnits);
  // A row can carry a bulk count (stock items, and accessories received by quantity).
  // A serialized unit leaves stock quantity at 1, so it still counts as one.
  // Deployed rows are in use; repair, out of order, and in-route are unavailable.
  let availability = 0;
  let inUse = 0;
  let unavailable = 0;
  for (const u of list) {
    const qty = stockRowQuantity(u);
    const s = u.status;
    if (s === 'Deployed' || s === 'Event') inUse += qty;
    else if (s === 'Repair' || s === 'Out of Order' || s === 'In-Route') unavailable += qty;
    else availability += qty;
  }
  return { availability, inUse, unavailable };
}

/** Only products with a buffer set appear here: when availability < product.lowStockBuffer. Sorted by availability ascending. */
export function getLowStockProducts(productsArg?: InventoryProduct[], allUnits?: InventoryUnit[]): { product: InventoryProduct; availability: number; threshold: number }[] {
  const products = productsArg ?? getInventoryProducts();
  const result: { product: InventoryProduct; availability: number; threshold: number }[] = [];
  for (const product of products) {
    if (product.catalogStatus === 'Void') continue;
    const buffer = product.lowStockBuffer;
    if (buffer == null || buffer <= 0) continue;
    const { availability } = getProductAvailabilityAndInUse(product.id, allUnits, product.type);
    if (availability < buffer) {
      result.push({ product, availability, threshold: buffer });
    }
  }
  result.sort((a, b) => a.availability - b.availability);
  return result;
}

/** Low-stock parts from TIM-E and BIM-E parts inventory: only rows with lowStockBuffer set and available < buffer. */
export type LowStockPart = {
  productName: string;
  availability: number;
  threshold: number;
  source: 'tim-e' | 'bim-e';
  /** Set when the row names a catalog product: status and counts were taken from that product. */
  productId?: string;
};

type PartsRow = {
  product: string;
  available: string;
  lowStockBuffer?: number | null;
  status?: 'Active' | 'Void';
};

/**
 * Voided parts rows are ignored outright. Beyond that, the parts tables predate the catalog and
 * still carry rows for items that now live in it, with a frozen `available` string. Where a row
 * names a catalog product, that product decides: voided items drop off, and the live unit count
 * replaces the frozen text. Rows with no catalog match (build parts such as PCB or Rasberry Pi)
 * keep using their own numbers.
 */
export function getLowStockParts(
  timERowsArg?: PartsRow[],
  bimERowsArg?: PartsRow[],
  productsArg?: InventoryProduct[],
  allUnits?: InventoryUnit[]
): LowStockPart[] {
  const timERows = timERowsArg ?? (getTimEPartsInventory() as PartsRow[]);
  const bimERows = bimERowsArg ?? (getBimEPartsInventory() as PartsRow[]);
  const products = productsArg ?? getInventoryProducts();
  const result: LowStockPart[] = [];
  const parseAvailable = (s: string) => Math.max(0, parseInt(String(s).trim(), 10) || 0);

  const catalogByName = new Map<string, InventoryProduct>();
  for (const p of products) {
    const key = p.name.trim().toLowerCase();
    if (key && !catalogByName.has(key)) catalogByName.set(key, p);
  }

  const evaluate = (row: PartsRow, source: 'tim-e' | 'bim-e'): LowStockPart | null => {
    if (row.status === 'Void') return null;
    const buffer = row.lowStockBuffer;
    if (buffer == null || buffer <= 0) return null;
    const match = catalogByName.get(String(row.product).trim().toLowerCase());
    if (!match) {
      const availability = parseAvailable(row.available);
      return availability < buffer ? { productName: row.product, availability, threshold: buffer, source } : null;
    }
    if (match.catalogStatus === 'Void') return null;
    // A buffer on the catalog product means getLowStockProducts already reports it.
    if (match.lowStockBuffer != null && match.lowStockBuffer > 0) return null;
    const { availability } = getProductAvailabilityAndInUse(match.id, allUnits, match.type);
    return availability < buffer
      ? { productName: match.name, availability, threshold: buffer, source, productId: match.id }
      : null;
  };

  for (const row of timERows) {
    const hit = evaluate(row, 'tim-e');
    if (hit) result.push(hit);
  }
  for (const row of bimERows) {
    const hit = evaluate(row, 'bim-e');
    if (hit) result.push(hit);
  }
  result.sort((a, b) => a.availability - b.availability);
  return result;
}

/** Robot fleet stats from TIM-E and BIM-E bot units in inventory (for Dashboard and Robots page). Excludes TIM-E Charger. */
export function getRobotFleetStats(productsArg?: InventoryProduct[], allUnits?: InventoryUnit[]): { deployed: number; inStorage: number; needsMaintenance: number } {
  const products = (productsArg ?? getInventoryProducts()).filter(
    (p) => p.type === 'Robot' && p.name !== 'TIM-E Charger' && p.catalogStatus !== 'Void'
  );
  let deployed = 0;
  let inStorage = 0;
  let needsMaintenance = 0;
  for (const product of products) {
    const units = getUnitsByProductId(product.id, allUnits);
    for (const u of units) {
      const status = u.status;
      if (status === 'Deployed' || status === 'Event') deployed += 1;
      else if (status === 'Repair' || status === 'Out of Order' || status === 'In-Route') needsMaintenance += 1;
      else inStorage += 1; // 'In Storage' or undefined
    }
  }
  return { deployed, inStorage, needsMaintenance };
}

export type UpdateInventoryUnitUpdates = {
  countryOfOrigin?: string;
  productType?: string;
  model?: string;
  serialNumber?: string;
  status?: InventoryUnitStatus;
  condition?: InventoryUnitCondition;
  manufacturer?: string;
  location?: string;
  business?: string;
  installationDate?: string;
  trashBins?: number;
  linenBins?: number;
  rollingBases?: number;
  staticBases?: number;
  stockQuantity?: number;
};

export function updateInventoryUnit(unitId: string, updates: UpdateInventoryUnitUpdates): InventoryUnit | null {
  const units = getInventoryUnits();
  const index = units.findIndex((u) => u.id === unitId);
  if (index === -1) return null;
  const next = { ...units[index], ...updates };
  if (updates.countryOfOrigin !== undefined) next.countryOfOrigin = updates.countryOfOrigin.trim();
  if (updates.productType !== undefined) next.productType = updates.productType.trim() || undefined;
  if (updates.model !== undefined) next.model = updates.model.trim();
  if (updates.serialNumber !== undefined) next.serialNumber = updates.serialNumber.trim();
  if (updates.manufacturer !== undefined) next.manufacturer = updates.manufacturer.trim() || undefined;
  if (updates.location !== undefined) next.location = updates.location.trim() || undefined;
  if (updates.business !== undefined) next.business = updates.business.trim() || undefined;
  if (updates.installationDate !== undefined) next.installationDate = updates.installationDate.trim() || undefined;
  if (updates.stockQuantity !== undefined) {
    const q = Math.floor(Number(updates.stockQuantity));
    next.stockQuantity = Number.isFinite(q) && q > 0 ? q : 1;
  }
  units[index] = next;
  setInventoryUnits(units);
  return next;
}

export function deleteInventoryUnit(unitId: string): boolean {
  const units = getInventoryUnits().filter((u) => u.id !== unitId);
  if (units.length === getInventoryUnits().length) return false;
  setInventoryUnits(units);
  return true;
}
